// handleGoalProgressUpdated.js
'use strict';
import {
  ADD_GOAL,
  REMOVE_LIST_ITEMS,
  MERGE_LIVE_STREAM_DATA,
  SET_LIVE_STREAM_DATA,
} from '../ActionTypes.js';

import getOperationData from '../selector/getOperationData.js';
import getMeData from '../selector/getMeData.js';

import updateTriggerPrivateUnlockState from '../action/updateTriggerPrivateUnlockState.js';
import handleGoalAdded from '../action/handleGoalAdded.js';
import removeModals from '../action/removeModals.js';
import { type, karaokeType } from '../resource/goalConstants.js';
import { formatGoalBreakdown } from '../resource/livestreamUtils.js';

/**
 * Handle stream goal completed
 * @kind action
 * @param {string} {goalId} - goal id.
 * @param {string} {streamId} - stream id.
 * @param {number} {progress} - stream progress.
 * @return {Promise} Action promise.
 */
const handleGoalProgressUpdated =
  ({ goalId, streamId, progress = 0, ...goalData }) =>
  async (dispatch, getState) => {
    if (!goalId) {
      return dispatch({ type: '' });
    }
    const goalType = goalData?.context?.type;
    if (goalType === type.KARAOKE) {
      dispatch(handleGoalAdded({ streamId, goalId, progress, ...goalData }));
      const isCompleted = progress >= goalData?.levels?.[0]?.target;
      const isPaused = !!goalData?.context?.paused;

      if (isCompleted) {
        const isToy = !!goalData?.context?.device;
        dispatch({
          type: REMOVE_LIST_ITEMS,
          payload: {
            selectPath: [
              'liveStream',
              streamId,
              'goal',
              isToy ? karaokeType.TOY_DEVICE : karaokeType.DEFAULT,
            ],
            itemIds: [goalId],
          },
        });
        return dispatch({
          type: ADD_GOAL,
          payload: {
            id: goalId,
            started: false,
          },
        });
      } else if (isPaused) {
        return dispatch({
          type: ADD_GOAL,
          payload: {
            id: goalId,
            started: false,
          },
        });
      } else {
        // Normal progress update
        return dispatch({ type: '' });
      }
    } else if (goalType === type.SHOW) {
      const isCompleted = progress >= goalData?.levels?.[0]?.target;

      if (isCompleted) {
        const activeStreamId = getOperationData(
          getState(),
          ['liveStream'],
          'activeStreamId'
        );

        if (activeStreamId === streamId) {
          dispatch(
            removeModals({
              ids: ['PrivateShowBuyTicketModal'],
              transitionStatus: 'closing',
            })
          );
        }

        dispatch({
          type: SET_LIVE_STREAM_DATA,
          payload: {
            selectPath: [streamId, 'privateShow'],
            data: {},
          },
        });
      } else {
        // user subscribe private-user after private show started, channel data only have goal.progress.updated (preview). update isPerforming to true, if perform goal not completed.
        dispatch({
          type: MERGE_LIVE_STREAM_DATA,
          payload: {
            selectPath: [streamId, 'privateShow'],
            data: {
              fundingId: goalData?.context?.['funding_goal_id'],
              performingId: goalId,
              status: {
                isPerforming: true,
              },
            },
          },
        });
      }
    } else if (goalType === type.SHOW_FUNDING) {
      // format breakdown when receive goal.progress.updated from presence-goal event.
      if (goalData?.breakdown) {
        return dispatch({
          type: ADD_GOAL,
          payload: {
            ...goalData,
            id: goalId,
            progress,
            breakdown: formatGoalBreakdown(goalData?.breakdown),
          },
        });
      }
    } else if (goalType === type.TRIGGER_PRIVATE) {
      dispatch({
        type: ADD_GOAL,
        payload: {
          ...goalData,
          id: goalId,
          progress,
          ...(goalData?.breakdown
            ? {
                breakdown: formatGoalBreakdown(goalData?.breakdown),
              }
            : {}),
        },
      });
      return dispatch(
        updateTriggerPrivateUnlockState({
          streamId,
          goalId,
          needFetchGoalBreakdown: false,
        })
      );
    } else if (goalType === type.TRIGGER_EXCLUSIVE) {
      const meId = getMeData(getState(), 'id');
      const isGoalCreator =
        meId != null && meId === goalData?.context?.exclusive_to;
      if (isGoalCreator) {
        dispatch({
          type: ADD_GOAL,
          payload: {
            ...goalData,
            id: goalId,
            progress,
            ...(goalData?.breakdown
              ? {
                  breakdown: formatGoalBreakdown(goalData?.breakdown),
                }
              : {}),
          },
        });
        return dispatch(
          updateTriggerPrivateUnlockState({
            streamId,
            goalId,
            needFetchGoalBreakdown: false,
          })
        );
      }
    }
    // update goal progress
    return dispatch({
      type: ADD_GOAL,
      payload: {
        ...goalData,
        id: goalId,
        progress,
        ...(goalData?.breakdown
          ? {
              breakdown: formatGoalBreakdown(goalData?.breakdown),
            }
          : {}),
      },
    });
  };

export default handleGoalProgressUpdated;
